/**
 * useBhiSnapshot.js — iter845 (Feb 2026)
 *
 * Extracted from BreastHealthIntelligencePage.jsx. Fetches the BHI
 * snapshot from `/api/breast-intelligence/snapshot` and refetches on
 * `conditions-changed` events so BDI/twin/ecosystem stay in sync when
 * the user edits diagnoses.
 *
 * Usage:
 *   const { data, loading, err, refresh, refreshKey } = useBhiSnapshot();
 *
 * `refreshKey` bumps whenever a conditions-changed refetch happens — pass
 * it into SpecLabPanel / other sub-modules to trigger their own refresh.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useHealthEvent } from "@/utils/healthEvents";

export default function useBhiSnapshot() {
  const { API, user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(async (opts = {}) => {
    // Only show the full-page loading spinner on initial load, not on
    // background re-fetches triggered by conditions-changed. Otherwise the
    // parent tree unmounts and any open modal disappears mid-interaction.
    const isBackground = opts.background === true;
    if (!isBackground) setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/breast-intelligence/snapshot`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (!authLoading && user) refresh();
  }, [authLoading, user, refresh]);

  // Live re-compute when a structured health condition changes.
  // Use background=true so open modals / children don't unmount mid-flow.
  useHealthEvent("conditions-changed", () => {
    if (user) {
      refresh({ background: true });
      setRefreshKey((k) => k + 1);
    }
  });

  return { data, loading, err, refresh, refreshKey, bumpRefreshKey: () => setRefreshKey((k) => k + 1), authLoading, user };
}
